// Audio recording helpers shared by Practice and AccentTest pages

// Convert audio blob to WAV format using Web Audio API
export const convertToWav = async (blob) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const arrayBuffer = await blob.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  // Convert to WAV
  const numberOfChannels = 1; // Mono
  const sampleRate = 16000; // 16kHz
  const length = audioBuffer.duration * sampleRate;
  const offlineContext = new OfflineAudioContext(numberOfChannels, length, sampleRate);

  const source = offlineContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offlineContext.destination);
  source.start();

  const renderedBuffer = await offlineContext.startRendering();

  return audioBufferToWav(renderedBuffer);
};

// Convert AudioBuffer to WAV Blob
const audioBufferToWav = (buffer) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;

  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;

  const data = [];
  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = buffer.getChannelData(channel)[i];
      const intSample = Math.max(-1, Math.min(1, sample));
      data.push(intSample < 0 ? intSample * 0x8000 : intSample * 0x7FFF);
    }
  }

  const dataLength = data.length * bytesPerSample;
  const buffer32 = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer32);

  // Write WAV header
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);

  // Write audio data
  let offset = 44;
  for (let i = 0; i < data.length; i++) {
    view.setInt16(offset, data[i], true);
    offset += 2;
  }

  return new Blob([buffer32], { type: 'audio/wav' });
};

// Flag emoji for each accent label the backend can return
export const ACCENT_FLAGS = {
  American: '\u{1F1FA}\u{1F1F8}',
  British: '\u{1F1EC}\u{1F1E7}',
  Australian: '\u{1F1E6}\u{1F1FA}',
  Indian: '\u{1F1EE}\u{1F1F3}',
  Chinese: '\u{1F1E8}\u{1F1F3}',
  Japanese: '\u{1F1EF}\u{1F1F5}',
  Korean: '\u{1F1F0}\u{1F1F7}',
  Vietnamese: '\u{1F1FB}\u{1F1F3}',
  Arabic: '\u{1F1F8}\u{1F1E6}',
  Turkish: '\u{1F1F9}\u{1F1F7}',
  Persian: '\u{1F1EE}\u{1F1F7}',
  Russian: '\u{1F1F7}\u{1F1FA}',
  Polish: '\u{1F1F5}\u{1F1F1}',
  Greek: '\u{1F1EC}\u{1F1F7}',
  Dutch: '\u{1F1F3}\u{1F1F1}',
  German: '\u{1F1E9}\u{1F1EA}',
  French: '\u{1F1EB}\u{1F1F7}',
  Italian: '\u{1F1EE}\u{1F1F9}',
  Spanish: '\u{1F1EA}\u{1F1F8}',
  Portuguese: '\u{1F1F5}\u{1F1F9}',
};
